/**
 * ChatBubble — floating button that opens the KI-Assistent drawer
 * (assistant/CockpitAssistant). Hidden while the drawer is open (its own
 * close button and Esc close it) and on the conversations page. When the
 * drawer closes, the focus comes back to the button.
 *
 * Animated with framer-motion:
 * - Entrance: bounces in from below
 * - Idle: subtle breathing glow pulse
 * - Streaming (an answer runs while the drawer is closed): orbital ring
 * - Hover: scale up with elevated shadow
 *
 * Painted in the organization's brand colours (per-org branding), the Focke
 * blues without a branding.
 */

import React, { useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { brandingPalette, useBranding } from "@digitalisation/enlixy-comp";
import { useLocation } from "react-router-dom";
import { ASSISTANT_PAGE, useAssistantUi } from "./assistant/assistantUiStore";

const FOCKE = { brand: "#025a94", bright: "#037ac7" };

/** "#rrggbb" with an alpha, for the shadow and the ring. */
const rgba = (hex: string, alpha: number) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

/** The brand colours as hex values, so framer-motion can animate between them. */
function useBrandColours() {
  const branding = useBranding();
  return useMemo(() => {
    const palette = branding?.primary
      ? brandingPalette(branding.primary, branding.secondary)
      : null;
    return palette
      ? { brand: palette.light.brand, bright: palette.light.brandBright }
      : FOCKE;
  }, [branding]);
}

export const ChatBubble: React.FC = () => {
  const { pathname } = useLocation();
  const isOpen = useAssistantUi((s) => s.isOpen);
  const open = useAssistantUi((s) => s.open);
  const isStreaming = useAssistantUi((s) => s.streaming);
  const button = useRef<HTMLButtonElement>(null);
  const colours = useBrandColours();
  // Closing the drawer removes the focused element with it: hand the focus
  // back to the launcher, as the former open/close toggle kept it.
  const wasOpen = useRef(isOpen);
  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      const active = document.activeElement;
      if (!active || active === document.body) button.current?.focus();
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  if (isOpen || pathname === ASSISTANT_PAGE) return null;

  return (
    <motion.button
      ref={button}
      onClick={open}
      data-tour="assistant-button"
      className="fixed bottom-24 right-4 sm:bottom-6 sm:right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center outline-none border-0 cursor-pointer"
      style={{
        background: `linear-gradient(135deg, ${colours.brand}, ${colours.bright})`,
      }}
      // Entrance animation
      initial={{ scale: 0, y: 40, opacity: 0 }}
      animate={{ scale: 1, y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 260, damping: 20 }}
      whileHover={{ scale: 1.12 }}
      whileTap={{ scale: 0.9 }}
      aria-label="Open AI assistant"
    >
      {/* Breathing glow ring (idle) */}
      {!isStreaming && (
        <motion.span
          className="absolute inset-0 rounded-full"
          style={{ border: `2px solid ${rgba(colours.brand, 0.4)}` }}
          animate={{
            scale: [1, 1.25, 1],
            opacity: [0.6, 0, 0.6],
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      )}

      {/* Streaming orbital ring */}
      {isStreaming && (
        <>
          <motion.span
            className="absolute inset-[-3px] rounded-full"
            style={{
              border: "2px solid transparent",
              borderTopColor: "#22d3ee",
              borderRightColor: "#22d3ee",
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <motion.span
            className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-cyan-400"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [1, 0.6, 1],
            }}
            transition={{ duration: 0.8, repeat: Infinity }}
          />
        </>
      )}

      {/* Shadow layer */}
      <motion.span
        className="absolute inset-0 rounded-full pointer-events-none"
        animate={{
          boxShadow: isStreaming
            ? "0 4px 25px rgba(34, 211, 238, 0.4), 0 0 40px rgba(34, 211, 238, 0.15)"
            : `0 4px 20px ${rgba(colours.brand, 0.35)}, 0 0 30px ${rgba(colours.bright, 0.1)}`,
        }}
        transition={{ duration: 0.4 }}
      />

      {/* Chat icon */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="w-6 h-6 text-on-brand relative z-10"
        aria-hidden="true"
      >
        <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z" />
        <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 001.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0015.75 7.5z" />
      </svg>
    </motion.button>
  );
};

export default ChatBubble;
